<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Capacitor } from '@capacitor/core';
import { useUser } from '@/api/user/queries/use-user';
import { RouteName } from '@/router';
import type { ConsentPurpose } from '@/api/user/types/privacy-consents';
import { useAppToasts } from '@/hooks/use-app-toasts';
import {
  offeredConsentPurposes,
  shouldShowConsentPrompt,
  submitConsentPrompt,
} from '@/services/privacy-consent-choices';
import { hasPendingRevocations } from '@/services/privacy-consents';
import PrivacyConsentSheet from './privacy-consent-sheet.vue';

/**
 * Existing users: the consent sheet, once, when signed in and the account has not answered the
 * current consent prompt. New users answer it during onboarding (questionnaire), so it is only
 * shown inside the main app, and never over a workout or live session in progress. The app
 * disables it while its own dialogs (force update, push permission, workout recovery) are up.
 */
const props = defineProps<{ enabled: boolean }>();

// Screens the prompt must not interrupt: the workout player (and its finish / feedback steps).
const BUSY_SEGMENTS = new Set<string>([RouteName.WorkoutPlayer]);
const isBusyRoute = (path: string) => path.split('/').some(segment => BUSY_SEGMENTS.has(segment));

const route = useRoute();
const { t } = useI18n();
const { raiseErrorToast } = useAppToasts();
const { data: user } = useUser();
const isOpen = ref(false);
const busy = ref(false);
const purposes = ref<ConsentPurpose[]>([]);
let openedFor: { accountId: string; dateOfBirth: string | null } | null = null;
// One attempt per account per app session: an unsaved answer (e.g. offline) asks again next launch.
const attempted = new Set<string>();
const showIosNote = Capacitor.getPlatform() === 'ios';

const shouldOpen = computed(() => {
  const accountId = user.value?.id;
  if (!props.enabled || !accountId || attempted.has(accountId)) return false;
  if (!route.path.startsWith(`/${RouteName.App}/`) || isBusyRoute(route.path)) return false;
  return shouldShowConsentPrompt(accountId, user.value?.profile?.dateOfBirth);
});

watch(
  shouldOpen,
  open => {
    if (!open || isOpen.value || !user.value) return;
    const dateOfBirth = user.value.profile?.dateOfBirth ?? null;
    openedFor = { accountId: user.value.id, dateOfBirth };
    attempted.add(user.value.id);
    purposes.value = offeredConsentPurposes(dateOfBirth);
    isOpen.value = true;
  },
  { immediate: true }
);

const onContinue = async (choices: Partial<Record<ConsentPurpose, boolean>>) => {
  busy.value = true;
  const accountId = openedFor?.accountId;
  const saved = accountId ? await submitConsentPrompt(accountId, openedFor?.dateOfBirth, choices, 'prompt') : true;
  busy.value = false;
  // Switching a granted consent off was not saved: it stays off here, but the user must know.
  if (!saved && hasPendingRevocations(accountId)) {
    void raiseErrorToast(t('privacy_consents.revoke_error'), { duration: 6000 });
  }
  isOpen.value = false;
  openedFor = null;
};
</script>

<template>
  <privacy-consent-sheet
    :is-open="isOpen"
    :purposes="purposes"
    :show-ios-note="showIosNote"
    :busy="busy"
    @continue="onContinue"
  />
</template>
